import React, {
  ChangeEvent,
  ComponentType,
  FocusEvent,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  InputBaseComponentProps,
} from '@mui/material/InputBase';
import {
  ManipulateType,
  OpUnitType,
} from 'dayjs';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  isEmptyString,
} from '@populateui/utils';
import CalendarIcon from 'core/icons/calendar';
import Button from 'pages/Dashboard/components/Button';
import Popover from 'core/components/Popover';
import Calendar from 'core/components/DateTimePicker/base/Calendar';
import {
  parseFlexibleDate,
} from 'utils/date';
import {
  PickerProps,
} from 'core/components/DateTimePicker/types';
import DateTextField, {
 dateInputAntiAutofillAttributes,
} from 'core/components/DateTimePicker/inputs/DateTextField';
import TextField from '@mui/material/TextField';
import type {
  Matcher,
} from '@daypicker/react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

type DesktopPaperContentProps = {
  children: ReactNode;
  onChange?: (date: Dayjs) => void;
  value?: Dayjs | null;
  [key: string]: any;
};

type Props = {
  className?: string;
  containerClassName?: string;
  value?: Dayjs | string | Date | null;
  onChange: (value: Dayjs | null) => void;
  onBlur?: () => void;
  showDateButtons?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  accumulationUnit?: OpUnitType;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  error?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  inputFormat?: string;
  placeholder?: string;
  inputProps?: Partial<InputBaseComponentProps>;
  inputContainerClassName?: string;
  openOnFocus?: boolean;
  enforceMinMaxOnCommit?: boolean;
  onOutOfRangeDate?: () => void;
  onInputErrorChange?: (message: string | null) => void;
  invalidValueMessage?: string;
  views?: string[];
  slots?: {
    desktopPaperContent?: ComponentType<DesktopPaperContentProps>;
  };
  slotProps?: {
    desktopPaper?: Record<string, any>;
    desktopPaperContent?: Record<string, any>;
  };
} & PickerProps;

const defaultInputFormat = 'MM/DD/YYYY';

function isValidDayjsValue(value: unknown): value is Dayjs {
  return dayjs?.isDayjs(value) && value?.isValid();
}

function warnUnexpectedDateValue(context: string, value: unknown) {
  console.warn(`[SingleDatePicker] Ignoring unexpected date value in ${context}.`, value);
}

function toDayjsValue(value?: Dayjs | string | Date | null) {
  return isNil(value) || isEmptyString(value)
    ? null
    : dayjs(value);
}

function formatInputValue(value: Dayjs | null, inputFormat: string) {
  return value?.isValid() ? value.format(inputFormat) : '';
}

function parseInputValue(value: string, inputFormat: string) {
  if (isEmptyString(value)) {
    return null;
  }

  const directParse = dayjs(value, inputFormat, true);
  if (directParse.isValid()) {
    return directParse;
  }

  return parseFlexibleDate(value);
}

function isDateWithinBounds(
  value: Dayjs | null | undefined,
  minDate?: Dayjs | null,
  maxDate?: Dayjs | null,
) {
  if (isNil(value) || !value.isValid()) {
    return true;
  }

  return (
    (isNil(minDate) || !value.isBefore(minDate, 'day'))
    && (isNil(maxDate) || !value.isAfter(maxDate, 'day'))
  );
}

export default function DatePicker({
  className,
  containerClassName,
  value,
  onChange,
  onBlur,
  showDateButtons = true,
  disablePast,
  disableFuture,
  accumulationUnit = 'day',
  disabled = false,
  error = false,
  minDate,
  maxDate,
  inputRef,
  inputFormat = defaultInputFormat,
  placeholder,
  inputProps,
  inputContainerClassName,
  openOnFocus,
  enforceMinMaxOnCommit = false,
  onOutOfRangeDate,
  onInputErrorChange,
  invalidValueMessage,
  disabledDays,
  fromYear,
  toYear,
  fromMonth,
  toMonth,
  fromDate,
  toDate,
  slots,
  slotProps,
}: Props) {
  const pickerValue = useMemo(() => toDayjsValue(value), [value]);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const suppressNextFocusOpenRef = useRef(false);
  const [month, setMonth] = useState<Date | undefined>(pickerValue?.toDate());
  const [inputValue, setInputValue] = useState(
    formatInputValue(pickerValue, inputFormat),
  );
  const normalizedInputFormat = inputFormat.toUpperCase();
  const useMaskedDateInput = useMemo(
    () => normalizedInputFormat === defaultInputFormat || normalizedInputFormat === 'MM/YYYY',
    [normalizedInputFormat],
  );

  useEffect(() => {
    setInputValue(formatInputValue(pickerValue, inputFormat));
    if (!open) {
      setMonth(pickerValue?.toDate());
    }
  }, [pickerValue, inputFormat, open]);

  const closePopover = () => {
    setOpen(false);
  };

  const openPopoverFromFocus = () => {
    if (suppressNextFocusOpenRef.current) {
      suppressNextFocusOpenRef.current = false;
      return;
    }

    setOpen(true);
  };

  const suppressNextFocusOpen = () => {
    suppressNextFocusOpenRef.current = true;
    window.setTimeout(() => {
      suppressNextFocusOpenRef.current = false;
    }, 100);
  };

  const commitDate = (newValue?: Dayjs | null) => {
    if (disabled) {
      return;
    }

    if (isNil(newValue)) {
      onChange(null);
      onBlur?.();
      setInputValue('');
      return;
    }

    if (!isValidDayjsValue(newValue)) {
      warnUnexpectedDateValue('commitDate', newValue);
      setInputValue(formatInputValue(pickerValue, inputFormat));
      return;
    }

    if (
      enforceMinMaxOnCommit
      && !isDateWithinBounds(newValue, minDate, maxDate)
    ) {
      setInputValue(formatInputValue(pickerValue, inputFormat));
      onOutOfRangeDate?.();
      onBlur?.();
      return;
    }

    const formattedNewValue = formatInputValue(newValue, inputFormat);

    if (pickerValue?.isValid() && pickerValue.isSame(newValue, 'day')) {
      onBlur?.();
      setInputValue(formattedNewValue);
      return;
    }

    onChange(newValue);
    onBlur?.();
    setInputValue(formattedNewValue);
  };

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const currentFormatted = formatInputValue(pickerValue, inputFormat);
    const nextValue = event.target.value;

    if (isEmptyString(nextValue)) {
      commitDate(null);
    } else if (nextValue !== currentFormatted) {
      const parsed = parseInputValue(nextValue, inputFormat);
      if (!isNil(parsed) && parsed.isValid()) {
        commitDate(parsed);
      } else {
        setInputValue(currentFormatted);
      }
    } else {
      setInputValue(currentFormatted);
    }

    onBlur?.();
  };

  const handleMonthSelect = (date: Date | undefined) => {
    if (!isNil(date)) {
      suppressNextFocusOpen();
      closePopover();
      commitDate(dayjs(date));
    }
  };

  const handleDesktopPaperContentChange = (date: Dayjs | null | undefined) => {
    if (!isNil(date) && !isValidDayjsValue(date)) {
      warnUnexpectedDateValue('desktopPaperContentChange', date);
      return;
    }

    suppressNextFocusOpen();
    closePopover();
    commitDate(date);
  };

  const handleMaskedDateChange = (dateValue: string | Date | null | undefined) => {
    const nextDate = dateValue instanceof Date
      ? dayjs(dateValue)
      : parseInputValue(dateValue?.toString() ?? '', inputFormat);

    if (isNil(nextDate)) {
      commitDate(null);
    } else if (nextDate.isValid()) {
      commitDate(nextDate);
    }
  };

  const disabledMatchers = useMemo(() => {
    const matchers: Matcher[] = [];

    if (!isNil(disabledDays)) {
      if (Array.isArray(disabledDays)) {
        matchers.push(...disabledDays);
      } else {
        matchers.push(disabledDays);
      }
    }
    if (disablePast) {
      matchers.push({ before: dayjs().startOf('day').toDate() });
    }
    if (disableFuture) {
      matchers.push({ after: dayjs().endOf('day').toDate() });
    }
    if (!isNil(minDate)) {
      matchers.push({ before: dayjs(minDate).startOf('day').toDate() });
    }
    if (!isNil(maxDate)) {
      matchers.push({ after: dayjs(maxDate).endOf('day').toDate() });
    }

    return matchers;
  }, [disabledDays, disablePast, disableFuture, minDate, maxDate]);

  const calendarDisabledDays = useMemo(() => (
    disabledMatchers.length > 0 ? disabledMatchers : undefined
  ), [disabledMatchers]);

  const calendarMinMonth = fromDate
    ?? minDate?.toDate()
    ?? (disablePast ? dayjs().startOf('day').toDate() : undefined)
    ?? fromMonth
    ?? (fromYear ? new Date(fromYear, 0, 1) : undefined);

  const calendarMaxMonth = toDate
    ?? maxDate?.toDate()
    ?? (disableFuture ? dayjs().endOf('day').toDate() : undefined)
    ?? toMonth
    ?? (toYear ? new Date(toYear, 11, 31) : undefined);

  const DesktopPaperContent = slots?.desktopPaperContent;
  const paperProps = slotProps?.desktopPaper ?? {};
  const paperSx = {
    ...(paperProps?.sx ?? {}),
    width: 'fit-content',
    minWidth: 'unset',
  };

  const calendar = (
    <Calendar
      mode="single"
      compact
      classNames={{
        selected: 'day_single_selected',
        today: 'day_single_today',
      }}
      month={month}
      onMonthChange={setMonth}
      selected={pickerValue?.toDate()}
      onSelect={handleMonthSelect}
      disabled={calendarDisabledDays}
      fromDate={calendarMinMonth}
      toDate={calendarMaxMonth}
      initialFocus
      className="w-fit shrink-0"
      formatters={{
        formatWeekdayName: (date: Date) => dayjs(date).format('ddd').substring(0, 1),
      }}
    />
  );

  const onNav = (direction: 1 | -1) => {
    if (pickerValue?.isValid()) {
      const nextValue = direction > 0
        ? pickerValue.endOf(accumulationUnit).add(1, 'day').startOf('day')
        : pickerValue.startOf(accumulationUnit).subtract(1, accumulationUnit as ManipulateType);
      commitDate(nextValue);
    }
  };

  return (
    <div
      ref={anchorRef}
      className={clsx(
        'populate-field-shell inline-flex w-fit items-stretch overflow-hidden',
        {
          'is-composite': showDateButtons,
          'has-error': error,
          'pointer-events-none': disabled,
        },
        containerClassName,
      )}
    >
      {showDateButtons && (
        <Button
          className={clsx(
            '!h-[34px] !min-w-[34px] !px-0 !py-0 !rounded-none !border-0 !border-r !border-r-gray-200 !bg-white !text-gray-700',
            'hover:!bg-gray-50',
            'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-default-input-focused',
          )}
          type="button"
          variant="outlined"
          disabled={disabled}
          onClick={() => { onNav(-1); }}
        >
          <KeyboardArrowLeftIcon className="!w-5 !h-5 !text-gray-700" />
        </Button>
      )}

      <div className={clsx('populate-date-field relative min-w-0 flex-none', inputContainerClassName ?? 'w-32')}>
        <button
          type="button"
          className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center text-neutral-700"
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            setOpen((prev) => !prev);
          }}
          tabIndex={-1}
          aria-label="Open calendar"
        >
          <CalendarIcon className="!w-5 !h-5 !text-gray-700" />
        </button>
        {useMaskedDateInput ? (
          <DateTextField
            inputRef={inputRef}
            value={pickerValue?.toDate() ?? null}
            onChange={handleMaskedDateChange}
            maskType={normalizedInputFormat === 'MM/YYYY' ? 'monthYear' : 'date'}
            isValueAllowed={(nextValue) => {
              if (!enforceMinMaxOnCommit) {
                return true;
              }

              const nextDate = nextValue instanceof Date
                ? dayjs(nextValue)
                : parseInputValue(nextValue?.toString() ?? '', inputFormat);

              return isDateWithinBounds(nextDate, minDate, maxDate);
            }}
            invalidValueMessage={invalidValueMessage}
            onInvalidValue={onOutOfRangeDate}
            onInputErrorChange={onInputErrorChange}
            onFocus={() => {
              if (openOnFocus) {
                openPopoverFromFocus();
              }
            }}
            onBlur={() => {
              onBlur?.();
            }}
            disabled={disabled}
            placeholder={placeholder ?? inputFormat.toLowerCase()}
            className={clsx(
              'populate-input populate-date-picker populate-date-picker-with-icon !m-0 !w-full !text-xs',
              className,
              { 'populate-date-picker-with-nav': showDateButtons },
            )}
            inputProps={inputProps}
          />
        ) : (
          <TextField
            fullWidth
            inputRef={inputRef}
            className={clsx(
              'populate-input populate-date-picker populate-date-picker-with-icon !m-0 !w-full !text-xs',
              className,
              { 'populate-date-picker-with-nav': showDateButtons },
            )}
            value={inputValue}
            disabled={disabled}
            placeholder={placeholder ?? inputFormat.toLowerCase()}
            onChange={handleTextChange}
            onFocus={() => {
              if (openOnFocus) {
                openPopoverFromFocus();
              }
            }}
            onClick={() => {
              if (openOnFocus) {
                setOpen(true);
              }
            }}
            onBlur={handleBlur}
            slotProps={{
              htmlInput: {
                ...dateInputAntiAutofillAttributes,
                ...inputProps,
                readOnly: inputProps?.readOnly,
              },
            }}
          />
        )}
      </div>

      {showDateButtons && (
        <Button
          className={clsx(
            '!h-[34px] !min-w-[34px] !px-0 !py-0 !rounded-none !border-0 !border-l !border-l-gray-200 !bg-white !text-gray-700',
            'hover:!bg-gray-50',
            'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-default-input-focused',
          )}
          type="button"
          variant="outlined"
          disabled={disabled}
          onClick={() => { onNav(1); }}
        >
          <KeyboardArrowRightIcon className="!w-5 !h-5 !text-gray-700" />
        </Button>
      )}

      <Popover
        anchorEl={open ? anchorRef.current : null}
        onClose={closePopover}
        transitionDuration={0}
        classes={{ paper: '!w-fit !min-w-0 !max-w-none !overflow-visible !shadow-[0px_0px_38px_-15px_rgba(0,_0,_0,_0.35)]' }}
        slotProps={{
          paper: {
            ...paperProps,
            sx: paperSx,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div className={clsx('populate-date-picker-popover inline-flex w-fit rounded-md border border-gray-100 bg-white', {
          'flex-row': !isNil(DesktopPaperContent),
        })}
        >
          {!isNil(DesktopPaperContent) ? (
            <DesktopPaperContent
              {...slotProps?.desktopPaperContent}
              onChange={handleDesktopPaperContentChange}
              value={pickerValue}
            >
              {calendar}
            </DesktopPaperContent>
          ) : (
            calendar
          )}
        </div>
      </Popover>
    </div>
  );
}
